import {
  ExhibitionConflictArtistData,
  ExhibitionConflictData,
} from '../dto/EntityRepository';
import {
  CreateExhibitionBulkItemReq,
  CreateExhibitionBulkReq,
  CreateExhibitionReq,
  ExhibitionArtistReq,
  ExhibitionArtistResolutionReq,
  ResolveExhibitionConflictsReq,
  UpdateExhibitionReq,
} from '../dto/ReqDtoRepository';

export interface ExhibitionArtistResolutionSelection {
  artistIndex: number;
  resolution: ExhibitionArtistResolutionReq;
}

/** 충돌한 행 전체에 Artist 선택 결과를 반영해 재요청 payload를 만든다. */
export const buildExhibitionConflictRetryReq = (
  item: CreateExhibitionBulkItemReq,
  selections: ExhibitionArtistResolutionSelection[],
): CreateExhibitionBulkReq => {
  const resolutionByIndex = new Map(
    selections.map(({ artistIndex, resolution }) => [artistIndex, resolution]),
  );

  for (const artistIndex of resolutionByIndex.keys()) {
    if (!Number.isInteger(artistIndex) || artistIndex < 0 || artistIndex >= item.exhibition.artists.length) {
      throw new RangeError(`Invalid artistIndex: ${artistIndex}`);
    }
  }

  return {
    exhibitionList: [{
      ...item,
      exhibition: {
        ...item.exhibition,
        artists: item.exhibition.artists.map((artist, artistIndex) => {
          const resolution = resolutionByIndex.get(artistIndex);
          return resolution ? { ...artist, ...resolution } : artist;
        }),
      },
    }],
  };
};

export const EXHIBITION_CONFLICT_FIELDS = [
  'title',
  'titleEn',
  'officialUrl',
  'sourceUrl',
  'artists',
] as const;

export type ExhibitionConflictField = typeof EXHIBITION_CONFLICT_FIELDS[number];
export type ExhibitionConflictChoice = 'EXISTING' | 'INCOMING';
export type ExhibitionConflictChoices = Partial<
  Record<ExhibitionConflictField, ExhibitionConflictChoice>
>;

export const getExhibitionConflictFields = (
  conflict: ExhibitionConflictData,
): ExhibitionConflictField[] =>
  EXHIBITION_CONFLICT_FIELDS.filter((field) => conflict[field] != null);

const toExistingArtistReq = (
  artist: ExhibitionConflictArtistData,
): ExhibitionArtistReq => {
  const input = {
    name: artist.name ?? '',
    ...(artist.nameEn ? { nameEn: artist.nameEn } : {}),
    ...(artist.instagramHandle ? { instagramHandle: artist.instagramHandle } : {}),
    ...(artist.architraveHandle ? { architraveHandle: artist.architraveHandle } : {}),
  };
  if (artist.artistId != null && artist.artistId > 0) {
    return {
      ...input,
      resolutionType: 'LINK_EXISTING',
      artistId: artist.artistId,
    };
  }
  return input;
};

export const buildResolvedExhibition = (
  incoming: CreateExhibitionReq,
  conflict: ExhibitionConflictData,
  choices: ExhibitionConflictChoices,
): UpdateExhibitionReq => {
  const unresolved = getExhibitionConflictFields(conflict)
    .filter((field) => choices[field] !== 'EXISTING' && choices[field] !== 'INCOMING');
  if (unresolved.length > 0) {
    throw new Error(`Unresolved exhibition conflict fields: ${unresolved.join(', ')}`);
  }

  const pick = (
    field: Exclude<ExhibitionConflictField, 'artists'>,
    incomingValue: string | undefined,
  ): string | undefined => {
    const diff = conflict[field];
    if (!diff) return incomingValue;
    const selected = choices[field] === 'EXISTING'
      ? diff.existing
      : incomingValue ?? diff.incoming;
    if (field === 'title' || field === 'sourceUrl') {
      return selected ?? '';
    }
    return selected || undefined;
  };

  const artists = conflict.artists && choices.artists === 'EXISTING'
    ? conflict.artists.existing.map(toExistingArtistReq)
    : incoming.artists;

  return {
    venueCode: incoming.venueCode,
    title: pick('title', incoming.title) ?? incoming.title,
    titleEn: pick('titleEn', incoming.titleEn),
    startDate: incoming.startDate,
    endDate: incoming.endDate,
    officialUrl: pick('officialUrl', incoming.officialUrl),
    sourceUrl: pick('sourceUrl', incoming.sourceUrl) ?? incoming.sourceUrl,
    artists,
  };
};

export const buildResolveExhibitionConflictsReq = (
  items: Array<{
    exhibitionId: number;
    incoming: CreateExhibitionReq;
    conflict: ExhibitionConflictData;
    choices: ExhibitionConflictChoices;
  }>,
): ResolveExhibitionConflictsReq => ({
  resolutions: items.map(({ exhibitionId, incoming, conflict, choices }) => ({
    exhibitionId,
    exhibition: buildResolvedExhibition(incoming, conflict, choices),
  })),
});
